import React, { useState, useCallback, useContext } from "react";
import { connect } from "react-redux";

import { Formik, FormikValues } from "formik";
import { Dispatch, bindActionCreators } from "redux";

import { Dialog, Form, CancelLink, OkButton, PickerInput, IDataColumnDefProps, DataColumnDef, ITextQuery, ValidationMessage, Text } from "@plex/react-components";

import { validationMessages } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { DialogContainer, DialogNavigationContext } from "ui/app/navigation";
import { useLocalizedValues, useLocalizer } from "ui/hooks";
import commonStyles from "ui/screens/translation-managers/shared/styles/Translations.module.scss";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";

import { plexTranslationMessagesActions } from "../PlexTranslationMessages.actions";
import { IAccount, IPlexTranslationMessagesManagerState } from "../PlexTranslationMessages.state";

import styles from "./AssigneePlexTranslationDialog.module.scss";

export interface IAssigneeDialog {
  accounts: IAccount[];
  notifications: INotification[];
  dialogTitle: string;
  currentAssignee?: string;
  loadPickerAccounts: (criterion: IPickerSearchCriterion<IAccount>) => void;
  onSelectionChanged: (selected?: IAccount) => void;
  actionPlexTranslations?: () => void;
  setValidationBanner: () => void;
  clearNotificationsActionBar: () => void;
}

export const AssigneePlexTranslationDialog: React.FC<IAssigneeDialog> = ({
  accounts,
  notifications,
  currentAssignee,
  dialogTitle,
  loadPickerAccounts,
  onSelectionChanged,
  actionPlexTranslations,
  setValidationBanner,
  clearNotificationsActionBar
}) => {
  const localizer = useLocalizer();

  const localizedValues = useLocalizedValues(() => {
    return {
      name: displayMessages.property("plexTranslationAssigneeName"),
      account: displayMessages.property("account"),
      assignee: displayMessages.title("assignee"),
      currentAssignee: displayMessages.title("currentAssignee")
    };
  });

  const requestExecute = useCallback(() => {
    actionPlexTranslations?.();
  }, [actionPlexTranslations]);

  const [selected, setSelected] = useState<IAccount[]>([]);

  const dialogNavigationContext = useContext(DialogNavigationContext);

  const close = useCallback(() => {
    clearNotificationsActionBar();
    dialogNavigationContext.hideDialog();
  }, [clearNotificationsActionBar, dialogNavigationContext]);

  return (
    <DialogContainer
      render={dialogProps => {
        return (
          <Dialog {...dialogProps} title={dialogTitle} minWidth={"400px"}>
            <Dialog.Body>
              <Banner messages={notifications} messageClassName={commonStyles.dialogBanner} />
              <Formik
                initialValues={{
                  account: undefined
                }}
                validate={values => {
                  const errors: any = {};
                  if (!values.account) {
                    errors.account = localizer(validationMessages.error("propertyIsRequired"), {
                      property: localizedValues.account
                    });
                    setValidationBanner();
                  } else {
                    clearNotificationsActionBar();
                  }

                  return errors;
                }}
                onSubmit={(_: FormikValues, { setSubmitting }) => {
                  requestExecute();
                  dialogNavigationContext.hideDialog();
                  setSubmitting(false);
                }}
              >
                {({ errors, setFieldValue, handleBlur, handleSubmit }) => (
                  <Form onSubmit={() => {}}>
                    <Form.Body>
                      <Form.Section title={localizedValues.account}>
                        <Form.Row>
                          <Form.FieldPair labelText={localizedValues.assignee} required className={styles.formFieldPair} id="accountId">
                            <PickerInput
                              name="account"
                              dialogTitle={localizedValues.account}
                              keySelector={(row: IAccount) => row.accountId}
                              displaySelector={(row: IAccount) => row.name}
                              selected={selected}
                              onBlur={handleBlur}
                              data={accounts}
                              onSelectionChanged={(items: IAccount[]) => {
                                setSelected(items);
                                onSelectionChanged?.(items[0] ? items[0] : undefined);
                                setFieldValue("account", items[0] ? items[0] : undefined);
                              }}
                              onSearch={(query: ITextQuery, columns: Array<IDataColumnDefProps<IAccount>>) => loadPickerAccounts({ query: query, searchedColumns: columns })}
                            >
                              <DataColumnDef<IAccount> key="column1" id="column1" searchable sortable title={localizedValues.name} valueSelector={row => row.name}>
                                {(row: IAccount) => row.name}
                              </DataColumnDef>
                            </PickerInput>
                            <ValidationMessage message={errors.account ? (errors.account as string) : ""} />
                          </Form.FieldPair>

                          {currentAssignee && (
                            <Form.FieldPair labelText={localizedValues.currentAssignee} className={styles.formFieldPair} id="currentAssignee">
                              <div className={styles.formFieldPairText}>
                                <Text text={currentAssignee}></Text>
                              </div>
                            </Form.FieldPair>
                          )}
                        </Form.Row>
                      </Form.Section>
                    </Form.Body>
                    <Form.Footer>
                      <CancelLink onClick={close}></CancelLink>
                      <OkButton onClick={() => handleSubmit()} />
                    </Form.Footer>
                  </Form>
                )}
              </Formik>
            </Dialog.Body>
          </Dialog>
        );
      }}
      onHidden={() => {
        clearNotificationsActionBar();
      }}
    />
  );
};

export default connect(
  (state: { plexTranslationMessagesManager: IPlexTranslationMessagesManagerState }) => {
    const actionBar = state.plexTranslationMessagesManager.actionBar;
    return {
      accounts: actionBar.accountPickerData,
      notifications: actionBar.notifications
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        loadPickerAccounts: plexTranslationMessagesActions.loadPickerAccounts,
        onSelectionChanged: plexTranslationMessagesActions.selectAccount,
        setValidationBanner: plexTranslationMessagesActions.setValidationBanner,
        clearNotificationsActionBar: plexTranslationMessagesActions.clearNotificationsActionBar
      },
      dispatch
    );
  }
)(AssigneePlexTranslationDialog);
